import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { LogService } from '@ups/xplat/core';
import { guid } from '@ups/xplat/utils';
import {
  dynamicFormGroupExcludedControlNames,
  IDynamicModel,
  IDynamicModelGroup,
  IDynamicModelProperties,
  IDynamicRegisterControl,
} from '../models';
import {
  dynamicAttachGateValidators,
  dynamicGateValidators,
  dynamicValidatorDate,
  dynamicValuePropertyFor,
  DynamicFreshControlState,
} from '../utils';

// various debug logs which can be isolated
const logTag = 'DynamicRenderService';

export interface IDynamicConfigGroupOptions {
  groupName?: string;
  groupDescription?: string;
  dynamicModels: Array<IDynamicModelProperties>;
  editing?: boolean;
  /**
   * generate unique formControlName's
   */
  generateFormControlNames?: boolean;
  submitButtonText?: string;
  addCancelButton?: boolean;
  addDraftButton?: boolean;
  closeOnCancel?: boolean;
  confirmCancelOnDirty?: boolean;
  cancelConfirmMessage?: string;
  /**
   * Enables controls to reorder fields, remove fields and disable editing.
   */
  isFormBuilder?: boolean;
  /**
   * Ignore adding a save or edit button
   * Used when configuring modals with your own save/edit/cancel buttons
   */
  ignoreDefaultButton?: boolean;
  /**
   * used to activate a single shared instance of the active model group
   */
  activateModelGroup?: boolean;
}

/** The model group DynamicRenderService.createConfigGroup returns (WI 31925: split out of it, unchanged). */
export function buildDynamicConfigGroup(
  options: IDynamicConfigGroupOptions,
  log: LogService
): IDynamicModelGroup {
  const controls: Array<IDynamicModelProperties> = [];
  let order = 0;
  for (const model of options.dynamicModels) {
    log.debug(logTag, 'dynamic model type:', model.type);
    const dynamicmodel: IDynamicModelProperties = {
      formControlName: options.generateFormControlNames
        ? guid()
        : model.formControlName,
      formArrayName: model.formArrayName,
      // reportCategoryName: model.reportCategoryName || model.formControlName,
      type: model.type,
      label: model.label || model.formControlName,
      instruction: model.instruction,
      types: model.types,
      selected: model.selected,
      value: model.value,
      disabled: model.disabled,
      ignoreValue: model.ignoreValue,
      required: model.required,
      placeholder: model.placeholder,
      inputType: model.inputType,
      valueProperty: dynamicValuePropertyFor(model), // pinned per type (category, vendor)
      // TODO: create validation mapping method to return validations based on fieldmodel types
      validators: model.validators || [],
      order,
      // Propagate form-level editing into each control so typeaheads can refuse
      // preSelectSingleFavorite on reopen (#30156). Only set when the caller passed
      // editing; admin form-builder omits it intentionally.
      options:
        options.editing === undefined
          ? model.options
          : {
              ...(model.options || {}),
              editing: options.editing,
            },
      isFormBuilder: options.isFormBuilder,
      clientId: model.clientId,
      isReusable: model.isReusable,
      readOnly: model.readOnly,
      reportCategoryName: model.reportCategoryName,
      accessRoleIds: model.accessRoleIds,
    };
    controls.push(dynamicmodel);
    order++;
  }
  // for now, always add a save or update button at the end
  if (!options.ignoreDefaultButton) {
    controls.push({
      formControlName: options.generateFormControlNames ? guid() : 'submit',
      type: 'button',
      value: options.submitButtonText || (options.editing ? 'Update' : 'Save'),
      order,
      options: {
        showCancelButton: options.addCancelButton,
        showDraftButton: options.addDraftButton,
        confirmCancelOnDirty: options.confirmCancelOnDirty,
        cancelConfirmMessage: options.cancelConfirmMessage,
        closeOnCancel: options.closeOnCancel,
        primary: true,
        submit: true,
      },
    });
  }

  return {
    groupType: 'GpType',
    groupName: options.groupName || '',
    groupDescription: options.groupDescription,
    controls: controls.sort((a, b) =>
      (a.order ?? 0) > (b.order ?? 0) ? 1 : -1
    ),
  };
}

/** Adds the control `config` describes to `group` with its validators (DynamicRenderService.registerControlForGroup). */
export function registerDynamicControl(
  config: IDynamicModel,
  group: UntypedFormGroup,
  freshControlState: DynamicFreshControlState
): IDynamicRegisterControl {
  const registerControlSettings: IDynamicRegisterControl = {};
  const formState: {
    disabled: boolean | undefined;
    value: string | number | boolean | Date | null;
  } = {
    disabled: config.disabled,
    value: null,
  };
  const controlLabel = config.label || config.formControlName || '';
  const formControlName = config.formControlName;

  if (config.value !== null && config.value !== undefined) {
    if (config.type.indexOf('typeahead') > -1) {
      // values are objects which match id's
      registerControlSettings.valueObjectId = <string>config?.value;
    } else if (config.type === 'date') {
      formState.value = new Date(<string>config.value);
    } else if (config.type === 'checkbox') {
      formState.value = config.value === 'true' || config.value === true;
    } else if (config.type === 'numerictextbox') {
      formState.value = Number(config.value);
    } else {
      formState.value = config.value;
    }
  }
  config.validators = [];
  const gateValidators = config.isFormBuilder
    ? []
    : dynamicGateValidators(config, controlLabel, group);
  if (!config.isFormBuilder) {
    if (config.required) {
      config.validators.push(Validators.required);
    }
    if (config.options?.maxlength) {
      config.validators.push(Validators.maxLength(config.options.maxlength));
    }
    if (config.options?.customValidations) {
      for (const validation of config.options.customValidations) {
        if (validation.startsWith('date:')) {
          config.validators.push(
            dynamicValidatorDate(controlLabel, validation)
          );
        }
      }
    }

    config.validators.push(...gateValidators);
  }
  if (
    formControlName &&
    !dynamicFormGroupExcludedControlNames.includes(formControlName)
  ) {
    // WI 35277: addControl keeps a control the host already owns (Mode A), so attach the
    // gate validators to it (a no-op unless the form opted in). addControl still runs for
    // every form: it re-validates the group and emits, which consumers rely on.
    const existing = group.controls[formControlName];
    if (existing) {
      dynamicAttachGateValidators(existing, gateValidators);
    }
    group.addControl(
      formControlName,
      new UntypedFormControl(formState, config.validators)
    );
    freshControlState.record(group.get(formControlName), config);
  }

  return registerControlSettings;
}

/** Clears the selected state of controls, their button groups and accordion children (DynamicRenderService.resetControlState). */
export function clearDynamicControlSelection(controls: IDynamicModel[]) {
  for (const control of controls) {
    control.selected = false;
    if (control.options?.buttongroup) {
      for (const bg of control.options.buttongroup) {
        bg.selected = false;
      }
    }
    if (control.options?.accordion) {
      for (const accordionControl of control.options.accordion) {
        accordionControl.selected = false;
        if (accordionControl.options?.buttongroup) {
          for (const bg of accordionControl.options.buttongroup) {
            bg.selected = false;
          }
        }
      }
    }
  }
}
